import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { esES } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { getContrastText } from "./utils";

import "./styles/animations.css";

const queryClient = new QueryClient();
const DEFAULT_PRIMARY_LIGHT = "#545ced";
const DEFAULT_PRIMARY_DARK = "#7c83fd";

const isValidThemeColor = (value) => {
  if (typeof value !== "string") return false;

  const color = value.trim();
  if (!color) return false;
  if (color.includes("<") || color.includes(">")) return false;

  if (
    typeof window !== "undefined" &&
    window.CSS &&
    typeof window.CSS.supports === "function"
  ) {
    return window.CSS.supports("color", color);
  }

  return (
    /^#([0-9a-f]{3,8})$/i.test(color) ||
    /^rgba?\(/i.test(color) ||
    /^hsla?\(/i.test(color)
  );
};

const sanitizeThemeColor = (value, fallback) =>
  isValidThemeColor(value) ? value.trim() : fallback;

const App = () => {
  const [locale, setLocale] = useState("esES");
  const storedPrimaryColorLight = localStorage.getItem("primaryColorLight");
  const storedPrimaryColorDark = localStorage.getItem("primaryColorDark");
  const storedBackgroundDark = localStorage.getItem("backgroundDark");
  const storedBackgroundPaperDark = localStorage.getItem("backgroundPaperDark");
  const storedFontFamily = localStorage.getItem("fontFamily") || '"Poppins", sans-serif';
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] = useState(
    sanitizeThemeColor(storedPrimaryColorLight, DEFAULT_PRIMARY_LIGHT)
  );
  const [primaryColorDark, setPrimaryColorDark] = useState(
    sanitizeThemeColor(storedPrimaryColorDark, DEFAULT_PRIMARY_DARK)
  );
  const [backgroundDark, setBackgroundDark] = useState(
    sanitizeThemeColor(storedBackgroundDark, "#0b141a")
  );
  const [backgroundPaperDark, setBackgroundPaperDark] = useState(
    sanitizeThemeColor(storedBackgroundPaperDark, "#111b21")
  );
  const [fontFamily, setFontFamily] = useState(storedFontFamily);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appLogoBackgroundLight, setAppLogoBackgroundLight] = useState("");
  const [appLogoBackgroundDark, setAppLogoBackgroundDark] = useState("");
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppLogoBackgroundLight,
      setAppLogoBackgroundDark,
      setAppName,
      setBackgroundDark,
      setBackgroundPaperDark,
      setFontFamily,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appLogoBackgroundLight,
      appLogoBackgroundDark,
      appName,
      backgroundDark,
      backgroundPaperDark,
      fontFamily,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode, backgroundDark, backgroundPaperDark, fontFamily]
  );

  const theme = useMemo(
    () => {
      const safePrimaryColorLight = sanitizeThemeColor(
        primaryColorLight,
        DEFAULT_PRIMARY_LIGHT
      );
      const safePrimaryColorDark = sanitizeThemeColor(
        primaryColorDark,
        DEFAULT_PRIMARY_DARK
      );
      const primaryMain =
        mode === "light" ? safePrimaryColorLight : safePrimaryColorDark;
      const contrastText = getContrastText(primaryMain);

      const darkBgDefault = sanitizeThemeColor(backgroundDark, "#0b141a");
      const darkBgPaper = sanitizeThemeColor(backgroundPaperDark, "#111b21");
      const darkBgMuted = darkBgDefault;
      const darkInputBg = darkBgPaper;
      const darkBorder = "rgba(255, 255, 255, 0.1)";
      const darkDivider = "rgba(148, 163, 184, 0.14)";
      const darkTextPrimary = "#E5E7EB";
      const darkTextSecondary = "#9CA3AF";

      const barraSuperiorBackground = mode === "light" ? primaryMain : darkBgPaper;
      const barraSuperiorContrastText = getContrastText(barraSuperiorBackground);

      return createTheme(
        {
          // Scrollbar styles melhorados
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.2)",
              backgroundColor: mode === "light" ? "#cbd5e1" : "#334155",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#94a3b8" : "#475569",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? "#f1f5f9" : darkBgMuted,
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#e2e8f0" : "#334155",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#cbd5e1" : "#475569",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

            palette: {
              type: mode,
            primary: {
              main: primaryMain,
              contrastText: contrastText,
            },
            secondary: {
              main: mode === 'light' ? '#64748b' : '#94a3b8',
              contrastText: '#ffffff',
            },
            background: {
              default: mode === 'light' ? '#F8FAFC' : darkBgDefault,
              paper: mode === 'light' ? '#FFFFFF' : darkBgPaper,
            },
            text: {
              primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
              secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.60)' : 'rgba(255, 255, 255, 0.60)',
              disabled: mode === 'light' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.38)',
            },
            ui: {
              surface: mode === "light" ? "#FFFFFF" : darkBgPaper,
              surfaceMuted: mode === "light" ? "#F8FAFC" : darkBgDefault,
              textMuted: mode === "light" ? "#475569" : "#94A3B8",
              border: mode === "light" ? "rgba(15, 23, 42, 0.16)" : darkBorder,
              borderStrong: mode === "light" ? "rgba(15, 23, 42, 0.26)" : "rgba(148, 163, 184, 0.28)",
              divider: mode === "light" ? "rgba(15, 23, 42, 0.2)" : darkDivider,
              chatSelected: mode === "light" ? "rgba(84, 92, 237, 0.16)" : "rgba(10, 132, 255, 0.18)",
              chatSelectedBorder: mode === "light" ? "rgba(84, 92, 237, 0.44)" : "rgba(10, 132, 255, 0.35)",
              chatUnread: mode === "light" ? "rgba(0, 98, 255, 0.12)" : "rgba(10, 132, 255, 0.25)",
            },
            action: {
              hover: mode === 'light' ? 'rgba(100, 116, 139, 0.08)' : 'rgba(148, 163, 184, 0.08)',
              selected: mode === 'light' ? 'rgba(84, 92, 237, 0.08)' : 'rgba(124, 131, 253, 0.12)',
              disabled: mode === 'light' ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
              disabledBackground: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
            },
            textPrimary: mode === "light" ? "#1E293B" : "#F8FAFC",
            textSecondary: mode === "light" ? "#334155" : "#94A3B8",
            borderPrimary: mode === "light" ? '#E2E8F0' : darkBorder,

            dark: { main: "#111418" },
            light: { main: "#F8FAFC" },

            fontColor: mode === "light" ? "#1E293B" : "#F8FAFC",
            tabHeaderBackground: mode === "light" ? "#F1F5F9" : darkBgPaper,
            optionsBackground: mode === "light" ? "#F8FAFC" : darkBgMuted,
            fancyBackground: mode === "light" ? '#F1F5F9' : darkBgMuted,
            total: mode === "light" ? "#1E293B" : "#F8FAFC",
            messageIcons: mode === "light" ? "#64748B" : "#94A3B8",
            inputBackground: mode === "light" ? "#FFFFFF" : darkInputBg,
            barraSuperior: barraSuperiorBackground,
            barraSuperiorContrastText: barraSuperiorContrastText,
            appLogoBackground: mode === "light" ? appLogoBackgroundLight : appLogoBackgroundDark,
          },

          typography: {
            fontFamily: fontFamily,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 600,
            h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em' },
            h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.005em' },
            h3: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.4, letterSpacing: '0em' },
            h4: { fontSize: '1.05rem', fontWeight: 500, lineHeight: 1.45, letterSpacing: '0em' },
            h5: { fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0em' },
            h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0em' },
            body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6, letterSpacing: '0em' },
            body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0em' },
            caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.02em' },
            button: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5, textTransform: 'uppercase', letterSpacing: '0.8px' },
          },

          shape: {
            borderRadius: 8,
          },

          overrides: {
            MuiButton: {
              root: {
                borderRadius: 8,
                fontWeight: 600,
                padding: '10px 24px',
                boxShadow: 'none',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: 'none',
                  transform: 'translateY(-1px)',
                }
              },
              containedPrimary: {
                boxShadow: mode === 'light'
                  ? '0 4px 12px rgba(84, 92, 237, 0.2)'
                  : '0 4px 12px rgba(0, 0, 0, 0.4)',
                '&:hover': {
                  boxShadow: mode === 'light'
                    ? '0 6px 16px rgba(84, 92, 237, 0.3)'
                    : '0 6px 16px rgba(0, 0, 0, 0.5)',
                  filter: 'brightness(1.1)',
                }
              },
              containedSecondary: {
                backgroundColor: mode === 'light' ? '#64748b' : '#334155',
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#475569' : '#1e293b',
                }
              },
              outlined: {
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                }
              }
            },
            MuiChip: {
              root: {
                borderRadius: 20,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                fontSize: '0.65rem',
              },
            },
            MuiTab: {
              root: {
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: 600,
              }
            },
            MuiPaper: {
              root: {
                backgroundImage: 'none',
              },
              rounded: {
                borderRadius: 8,
              },
              elevation1: {
                boxShadow: mode === 'light'
                  ? '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)'
                  : '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
              },
              elevation2: {
                boxShadow: mode === 'light'
                  ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -4px rgba(0, 0, 0, 0.2)',
              }
            },

            MuiInputBase: {
              input: {
                fontWeight: 400,
                color: mode === "light" ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
                "&::placeholder": {
                  color: mode === "light" ? "rgba(0, 0, 0, 0.60)" : "rgba(255, 255, 255, 0.60)",
                  opacity: 1,
                }
              }
            },
            
            MuiTableCell: {
              head: {
                fontWeight: 600,
                fontSize: "0.875rem",
                color: mode === "light" ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
              },
              body: {
                fontSize: "0.8125rem",
              }
            },
            
            MuiLink: {
              root: {
                color: mode === 'light' ? '#1565c0' : '#64b5f6',
                textDecoration: 'none',
                '&:hover': {
                  color: mode === 'light' ? '#0d47a1' : '#90caf9',
                  textDecoration: 'underline',
                  fontWeight: 500,
                }
              }
            },

            MuiInputLabel: {
              root: {
                color: mode === "light" ? "#334155" : "#94A3B8",
                "&.Mui-focused": {
                  color: mode === "light" ? "#1E293B" : "#F8FAFC",
                }
              }
            },

            MuiFormLabel: {
              root: {
                color: mode === "light" ? "#334155" : "#94A3B8",
              }
            },

            MuiCssBaseline: {
              "@global": {
                "*, *::before, *::after": {
                  boxSizing: "border-box",
                },
                html: {
                  height: ["100%", "100vh", "100dvh"],
                  overflowX: "hidden",
                  scrollbarGutter: "stable",
                },
                "#root": {
                  height: ["100%", "100vh", "100dvh"],
                  overflowX: "hidden",
                },
                body: {
                  minHeight: ["100%", "100vh", "100dvh"],
                  height: ["100%", "100vh", "100dvh"],
                  color: mode === "light" ? "#1E293B" : "#F8FAFC",
                  backgroundColor: mode === "light" ? "#F8FAFC" : darkBgDefault,
                  fontFamily: '"Outfit", "Plus Jakarta Sans", "Inter", "Roboto", sans-serif',
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  overflowX: "hidden",
                },
                "input[type='date'], input[type='time'], input[type='datetime-local']": {
                  colorScheme: mode,
                },
                ".MuiInputBase-input[type='date'], .MuiInputBase-input[type='time'], .MuiInputBase-input[type='datetime-local']": {
                  color: mode === "light" ? "#1E293B" : "#F8FAFC",
                },
                ".MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator, .MuiInputBase-input[type='datetime-local']::-webkit-calendar-picker-indicator": {
                  filter: mode === "light" ? "none" : "invert(1) opacity(0.9)",
                },
                ".MuiInputBase-input[type='date']::-webkit-datetime-edit, .MuiInputBase-input[type='time']::-webkit-datetime-edit, .MuiInputBase-input[type='datetime-local']::-webkit-datetime-edit": {
                  color: mode === "light" ? "#1E293B" : "#F8FAFC",
                },
                ".MuiInputBase-input[type='date']::-webkit-datetime-edit-text, .MuiInputBase-input[type='time']::-webkit-datetime-edit-text, .MuiInputBase-input[type='datetime-local']::-webkit-datetime-edit-text": {
                  color: mode === "light" ? "#475569" : "#CBD5E1",
                },
                "button:focus-visible, [role='button']:focus-visible, a:focus-visible, input:focus-visible, textarea:focus-visible, .MuiListItem-button.Mui-focusVisible": {
                  outline: `2px solid ${mode === "light" ? "rgba(84, 92, 237, 0.78)" : "rgba(148, 163, 255, 0.9)"}`,
                  outlineOffset: 2,
                },
                ".Toastify__toast": {
                  backgroundColor: mode === "light" ? "rgba(255, 255, 255, 0.85)" : "rgba(17, 27, 33, 0.85) !important",
                  backdropFilter: "blur(12px) saturate(180%)",
                  WebkitBackdropFilter: "blur(12px) saturate(180%)",
                  color: mode === "light" ? "#1E293B" : "#F8FAFC !important",
                  borderRadius: "16px !important",
                  boxShadow: mode === "light" 
                    ? "0 8px 32px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0,0,0,0.04)" 
                    : "0 8px 32px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0,0,0,0.2) !important",
                  border: `1px solid ${mode === "light" ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.05)"}`,
                  padding: "16px !important",
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 500,
                  fontSize: "14px",
                },
                ".Toastify__toast--success": {
                  border: `1px solid ${mode === "light" ? "rgba(34, 197, 94, 0.3)" : "rgba(34, 197, 94, 0.2)"} !important`,
                },
                ".Toastify__toast--error": {
                  border: `1px solid ${mode === "light" ? "rgba(239, 68, 68, 0.3)" : "rgba(239, 68, 68, 0.2)"} !important`,
                },
                ".Toastify__progress-bar": {
                  height: "4px !important",
                  borderBottomLeftRadius: "16px",
                  borderBottomRightRadius: "16px",
                },
                ".Toastify__progress-bar--success": {
                  background: "linear-gradient(90deg, #22c55e, #16a34a) !important",
                },
                ".Toastify__progress-bar--error": {
                  background: "linear-gradient(90deg, #ef4444, #dc2626) !important",
                },
                ".Toastify__toast-icon": {
                  width: "24px",
                  height: "24px",
                },
              },
            },

            MuiDrawer: {
              paper: {
                borderRight: 'none',
                backgroundColor: mode === 'light' ? '#ffffff' : darkBgMuted,
                borderRight: mode === 'light' ? '1px solid #e2e8f0' : `1px solid ${darkBorder}`,
              }
            },

            MuiAppBar: {
              root: {
                boxShadow: 'none',
                borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.05)'}`,
                backgroundColor: barraSuperiorBackground,
              },
              colorPrimary: {
                backgroundColor: barraSuperiorBackground,
                color: barraSuperiorContrastText,
              }
            },

            MuiMenu: {
              paper: {
                borderRadius: 12,
              }
            },

            MuiCard: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === 'light' ? '#ffffff' : darkBgPaper,
                border: mode === 'light' ? '1px solid #e2e8f0' : `1px solid ${darkBorder}`,
              }
            },

            MuiChip: {
              root: {
                fontWeight: 600,
              }
            },

            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: mode === 'light' ? '#ffffff' : darkInputBg,
                  '& fieldset': {
                    borderColor: mode === 'light' ? '#E2E8F0' : darkBorder,
                  },
                  '&:hover fieldset': {
                    borderColor: mode === 'light' ? '#94A3B8' : '#7c83fd',
                  },
                }
              }
            },

            MuiDialog: {
              paper: {
                backgroundImage: "none",
                backgroundColor: mode === "light" ? "#ffffff" : darkBgPaper,
                border: mode === "light"
                  ? "1px solid rgba(15, 23, 42, 0.12)"
                  : `1px solid ${darkBorder}`,
              }
            },

            MuiTableCell: {
              root: {
                borderBottom: `1px solid ${mode === "light" ? "rgba(15, 23, 42, 0.12)" : darkDivider}`,
              },
              head: {
                fontWeight: 700,
                color: mode === "light" ? "#0F172A" : "#F8FAFC",
                backgroundColor: mode === "light" ? "#F8FAFC" : darkBgMuted,
              },
              body: {
                color: mode === "light" ? "#1E293B" : darkTextPrimary,
              }
            },

            MuiIconButton: {
              root: {
                color: mode === "light" ? "#334155" : darkTextPrimary,
              }
            },

            MuiTooltip: {
              tooltip: {
                backgroundColor: mode === 'light'
                  ? 'rgba(30, 41, 59, 0.95)'
                  : 'rgba(248, 250, 252, 0.95)',
                color: mode === 'light' ? '#fff' : darkBgDefault,
                fontSize: '0.875rem',
                fontWeight: 500,
                padding: '8px 12px',
                borderRadius: 8,
                backdropFilter: 'blur(10px)',
                boxShadow: mode === 'light'
                  ? '0 4px 12px rgba(0,0,0,0.15)'
                  : '0 4px 12px rgba(0,0,0,0.4)',
              },
              arrow: {
                color: mode === 'light'
                  ? 'rgba(30, 41, 59, 0.95)'
                  : 'rgba(248, 250, 252, 0.95)',
              }
            },
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      );
    },
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight,
      appLogoBackgroundLight,
      appLogoBackgroundDark,
      backgroundDark,
      backgroundPaperDark,
      fontFamily,
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(
          sanitizeThemeColor(color, DEFAULT_PRIMARY_LIGHT)
        );
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(sanitizeThemeColor(color, DEFAULT_PRIMARY_DARK));
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
      });
    getPublicSetting("appLogoBackgroundLight")
      .then((color) => {
        setAppLogoBackgroundLight(color || "");
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
      });
    getPublicSetting("appLogoBackgroundDark")
      .then((color) => {
        setAppLogoBackgroundDark(color || "");
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Multi100");
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error reading setting", error);
        }
        setAppName("Multi100");
      });
    getPublicSetting("backgroundDark")
      .then((color) => {
        if (color) setBackgroundDark(sanitizeThemeColor(color, "#0b141a"));
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error("Error reading setting", error);
      });
    getPublicSetting("backgroundPaperDark")
      .then((color) => {
        if (color) setBackgroundPaperDark(sanitizeThemeColor(color, "#111b21"));
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error("Error reading setting", error);
      });
    getPublicSetting("fontFamily")
      .then((font) => {
        if (font) setFontFamily(font);
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error("Error reading setting", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const safePrimaryColorLight = sanitizeThemeColor(
      primaryColorLight,
      DEFAULT_PRIMARY_LIGHT
    );
    const safePrimaryColorDark = sanitizeThemeColor(
      primaryColorDark,
      DEFAULT_PRIMARY_DARK
    );
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? safePrimaryColorLight : safePrimaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error fetching data", error);
        }
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
